import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { Database } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { PUBLISHER_NAMES } from '@/graphql/account-signals-queries'
import { useAccountAudiences } from '@/hooks/useAccountAudiences'
import { metaPublisherIdOf } from '@/components/mediaBuying/adCampaign/screens/AudienceTargeting/Settings/LanguageSettings'
import {
    ACCOUNT_AUDIENCE_SUBTYPE,
    ACCOUNT_AUDIENCE_TEXT,
    accountAudienceNoteText,
    accountAudienceReasonText,
    accountAudienceRetentionText,
    accountAudienceSizeText,
    accountAudienceStatusHint,
    accountAudienceStatusLabel,
    accountAudienceSubtypeLabel,
    filterAccountAudiences,
    isAccountAudienceSelected,
    stageAudienceIds,
    withAccountAudience,
} from '@/utils/account-audiences'

interface AccountAudience {
    external_id: string
    name: string
    subtype: string
    approximate_count_lower: number | null
    retention_days?: number | null
    usable: boolean
    reason: string | null
    note: string | null
}

interface AccountAudiencesPanelProps {
    stageId: string
    data: Record<string, any>
    onChange: (data: Record<string, any>) => void
    isEditMode?: boolean
}

const formatFetchedAt = (value: string) => {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : format(date, 'd MMM yyyy, HH:mm')
}

/** The status badge of an audience; the trigger can be focused so the explanation is reachable by keyboard. */
const StatusBadge: React.FC<{ audience: AccountAudience }> = ({ audience }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span tabIndex={0} className="tw:inline-flex tw:rounded-md tw:cursor-help tw:focus-visible:tw:outline-2">
                <Badge variant={audience.usable ? 'secondary' : 'destructive'}>
                    {accountAudienceStatusLabel(audience)}
                </Badge>
            </span>
        </TooltipTrigger>
        <TooltipContent className="tw:max-w-xs">{accountAudienceStatusHint(audience)}</TooltipContent>
    </Tooltip>
)

/**
 * The audiences of the customer's ad account, chosen for the ad sets of one funnel stage. The list is the
 * server's stored copy; Meta is read only when nothing is stored yet or when the user asks for it. Audiences
 * that cannot be used stay in the list with the reason and cannot be chosen.
 */
const AccountAudiencesPanel: React.FC<AccountAudiencesPanelProps> = ({
    stageId,
    data,
    onChange,
    isEditMode = false,
}) => {
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()
    const hasConnection = !!connectedPublishers && Object.keys(connectedPublishers).length > 0
    const enabled = !isEditMode && hasConnection && !!campaignId

    const publishersResult = useQuery(PUBLISHER_NAMES, { fetchPolicy: 'cache-first', skip: !enabled })
    const publisherId = metaPublisherIdOf(connectedPublishers, publishersResult.data?.getPublishersFromPostgres)

    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [subtype, setSubtype] = useState('')
    const { audiences, stages, fetchedAt, complete, loading, refreshing, error, notice, refresh } = useAccountAudiences(
        {
            publisherId,
            campaignId,
            enabled: open,
        },
    )

    if (!enabled || !publisherId) return null

    const catalogueIds = stageAudienceIds(stages)
    const catalogueAudienceId = catalogueIds[stageId]
    const visible: AccountAudience[] = filterAccountAudiences(audiences, { search, subtype })
    const fetchedText = fetchedAt ? formatFetchedAt(fetchedAt) : null
    const chosenCount = (data?.audienceSelections?.[stageId] || []).filter((id: string) =>
        Object.keys(data?.accountAudiences || {}).includes(id),
    ).length

    const toggle = (audience: AccountAudience, checked: boolean) => {
        if (!catalogueAudienceId) return
        onChange(withAccountAudience(data, stageId, audience, catalogueAudienceId, checked))
    }

    return (
        <div className="tw:px-6" data-testid="account-audiences-panel">
            <Button variant="secondary" size="sm" className="tw:w-fit" onClick={() => setOpen(true)}>
                <Database className="tw:size-5" />
                {ACCOUNT_AUDIENCE_TEXT.button}
                {chosenCount > 0 ? ` (${chosenCount})` : ''}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="tw:sm:tw:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{ACCOUNT_AUDIENCE_TEXT.title}</DialogTitle>
                    </DialogHeader>
                    <DialogDescription>{ACCOUNT_AUDIENCE_TEXT.description}</DialogDescription>
                    <div className="tw:flex tw:flex-col tw:gap-3 tw:px-6 tw:pt-4">
                        <div className="tw:flex tw:items-center tw:gap-3 tw:flex-wrap">
                            <Button variant="secondary" size="sm" disabled={refreshing} onClick={refresh}>
                                {refreshing && (
                                    <div
                                        className="tw:w-4 tw:h-4 tw:flex-shrink-0 tw:border-2 tw:border-white tw:border-t-transparent tw:rounded-full tw:animate-spin"
                                        data-testid="account-audiences-spinner"
                                    />
                                )}
                                {refreshing ? ACCOUNT_AUDIENCE_TEXT.fetching : ACCOUNT_AUDIENCE_TEXT.fetch}
                            </Button>
                            {fetchedText && (
                                <span className="tw:text-xs tw:text-gray-200" data-testid="account-audiences-fetched">
                                    {`Last fetched ${fetchedText}`}
                                </span>
                            )}
                        </div>
                        {error && (
                            <p className="tw:text-sm tw:text-red-300" role="alert">
                                {error}
                            </p>
                        )}
                        {notice && !error && (
                            <p className="tw:text-sm tw:text-gray-200" role="status">
                                {notice}
                            </p>
                        )}
                        <Input
                            type="search"
                            aria-label={ACCOUNT_AUDIENCE_TEXT.searchLabel}
                            placeholder={ACCOUNT_AUDIENCE_TEXT.searchPlaceholder}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <div
                            className="tw:flex tw:gap-2 tw:flex-wrap"
                            role="group"
                            aria-label={ACCOUNT_AUDIENCE_TEXT.filterLabel}
                        >
                            {[['', ACCOUNT_AUDIENCE_TEXT.allTypes], ...Object.entries(ACCOUNT_AUDIENCE_SUBTYPE)].map(
                                ([value, label]) => (
                                    <Button
                                        key={value || 'all'}
                                        type="button"
                                        size="sm"
                                        variant={subtype === value ? 'default' : 'secondary'}
                                        aria-pressed={subtype === value}
                                        onClick={() => setSubtype(value)}
                                    >
                                        {label}
                                    </Button>
                                ),
                            )}
                        </div>
                    </div>
                    <div className="tw:flex tw:flex-col tw:max-h-96 tw:overflow-y-auto tw:px-6 tw:py-4 tw:gap-2">
                        {loading && (
                            <p className="tw:text-sm tw:text-gray-200" role="status">
                                {ACCOUNT_AUDIENCE_TEXT.loading}
                            </p>
                        )}
                        {!loading && audiences.length === 0 && !refreshing && !error && (
                            <p className="tw:text-sm tw:text-gray-200" data-testid="account-audiences-empty">
                                {ACCOUNT_AUDIENCE_TEXT.empty}
                            </p>
                        )}
                        {audiences.length > 0 && visible.length === 0 && (
                            <p className="tw:text-sm tw:text-gray-200">{ACCOUNT_AUDIENCE_TEXT.noMatch}</p>
                        )}
                        {visible.map((audience) => {
                            const checked = isAccountAudienceSelected(data, stageId, audience.external_id)
                            const disabled = (!audience.usable && !checked) || !catalogueAudienceId
                            const note = audience.usable ? accountAudienceNoteText(audience.note) : null
                            const retention = accountAudienceRetentionText(audience.retention_days)
                            const describedBy = `account-audience-${audience.external_id}-detail`
                            return (
                                <label
                                    key={audience.external_id}
                                    className="tw:flex tw:items-start tw:gap-3 tw:rounded-xs tw:border tw:p-3 tw:cursor-pointer tw:aria-disabled:tw:opacity-60 tw:aria-disabled:tw:cursor-not-allowed"
                                    aria-disabled={disabled}
                                    data-testid={`account-audience-${audience.external_id}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="tw:mt-1"
                                        checked={checked}
                                        disabled={disabled}
                                        aria-label={audience.name}
                                        aria-describedby={describedBy}
                                        onChange={(event) => toggle(audience, event.target.checked)}
                                    />
                                    <span className="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
                                        <span className="tw:text-sm tw:font-medium tw:break-words">
                                            {audience.name}
                                        </span>
                                        <span id={describedBy} className="tw:flex tw:flex-col tw:gap-1">
                                            <span className="tw:flex tw:gap-2 tw:flex-wrap tw:items-center">
                                                <Badge variant="campaign">
                                                    {accountAudienceSubtypeLabel(audience.subtype)}
                                                </Badge>
                                                <Badge variant="campaign">
                                                    {accountAudienceSizeText(audience.approximate_count_lower)}
                                                </Badge>
                                                {retention && <Badge variant="campaign">{retention}</Badge>}
                                                <StatusBadge audience={audience} />
                                                {checked && (
                                                    <Badge variant="default">{ACCOUNT_AUDIENCE_TEXT.selected}</Badge>
                                                )}
                                            </span>
                                            {!audience.usable && (
                                                <span className="tw:text-xs tw:text-yellow-300">
                                                    {accountAudienceReasonText(audience.reason)}
                                                </span>
                                            )}
                                            {note && <span className="tw:text-xs tw:text-gray-200">{note}</span>}
                                        </span>
                                    </span>
                                </label>
                            )
                        })}
                        {!complete && <p className="tw:text-xs tw:text-gray-200">{ACCOUNT_AUDIENCE_TEXT.incomplete}</p>}
                    </div>
                    <DialogFooter>
                        <Button size="sm" onClick={() => setOpen(false)}>
                            {ACCOUNT_AUDIENCE_TEXT.done}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default AccountAudiencesPanel
